<template>
  <div class="">
    <div class="p-field-horizontal">
      <FloatLabel :variant="variant" class="w-full">
        <IconField class="w-full p-icon-field">
          <InputIcon :class="icon" />
          <InputText ref="inputRef" :id="id" :model-value="modelValue" :class="[
            'p-inputtext p-component one-input w-full',
            {
              'p-filled': isFilled,
              'p-inputtext-sm': size === 'small',
              'p-inputtext-lg': size === 'large',
              'p-invalid': isInvalid,
              'p-disabled': disabled,
              [`p-variant-${variant}`]: variant,
              'direction-aware': true
            }
          ]" :disabled="disabled" :placeholder="placeholder" :autocomplete="autocomplete"
            :aria-label="props['aria-label']" :aria-labelledby="props['aria-labelledby']" :aria-invalid="isInvalid"
            :aria-required="required" :aria-describedby="ariaDescribedBy" :required="required" :readonly="readonly"
            v-bind="$attrs" @input="onInput" @focus="onFocus" @blur="onBlur" @keydown="onKeydown" @change="onChange" />
        </IconField>
        <label :for="id" class="label-text">{{ label }}</label>
      </FloatLabel>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages mt-1">
      <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
      <small v-for="(error, index) in localizedErrors" :key="index" :id="`${id}-error-${index}`" class="p-error block">
        {{ error }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import InputText from 'primevue/inputtext'
import FloatLabel from 'primevue/floatlabel'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'

export default defineComponent({
  name: 'ONEInputIcon',
  components: {
    InputText,
    FloatLabel,
    IconField,
    InputIcon
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { ONEInputIconProps, ONEInputIconEmits } from './types'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'

const props = withDefaults(defineProps<ONEInputIconProps>(), {
  modelValue: null,
  id: () => `one-input-${Math.random().toString(36).substr(2, 9)}`,
  label: '',
  icon: 'pi pi-search',
  variant: 'over',
  size: undefined,
  invalid: false,
  disabled: false,
  readonly: false,
  required: false,
  placeholder: '',
  helperText: '',
  autocomplete: 'off',
  filled: false,
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  showErrors: true,
  name: undefined,
  formContext: true,
  'aria-label': '',
  'aria-labelledby': ''
})

const emit = defineEmits<ONEInputIconEmits>()

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()

// Local State
const inputRef = ref<HTMLInputElement | null>(null)
const localValidationErrors = ref<string[]>([])
const isDirty = ref(false)
const isValidating = ref(false)

const isFilled = computed(() => {
  return props.modelValue != null && props.modelValue.toString().length > 0
})

const currentErrors = computed(() => {
  if (formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    return error
  })
})

const isInvalid = computed(() => {
  return props.invalid || (currentErrors.value.length > 0 && isDirty.value)
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  currentErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`))
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (!!props.helperText || (currentErrors.value.length > 0 && isDirty.value))
})

const validate = async (): Promise<boolean> => {
  if (formState && props.name) {
    return formState.validateField(props.name)
  }

  isValidating.value = true
  try {
    const errors: string[] = []
    const value = props.modelValue

    for (const rule of props.validationRules) {
      let isValid = true
      const message = rule.message

      if (rule.validator) {
        isValid = await rule.validator(value)
      }

      if (!isValid && message) {
        errors.push(message)
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

const onInput = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value === '' ? null : target.value
  emit('update:modelValue', value)
  emit('input', event)

  if (formState && props.name) {
    formState.setFieldValue(props.name, value)
  }

  isDirty.value = true
  if (props.validateOnInput) {
    await validate()
  }
}

const onFocus = (event: Event) => {
  emit('focus', event)
}

const onBlur = async (event: Event) => {
  emit('blur', event)
  isDirty.value = true
  if (formState && props.name) {
    formState.setFieldTouched(props.name, true)
  }
  if (props.validateOnBlur) {
    await validate()
  }
}

const onKeydown = (event: Event) => {
  emit('keydown', event)
}

const onChange = async (event: Event) => {
  emit('change', event)
  isDirty.value = true
  if (props.validateOnChange) {
    await validate()
  }
}

const exposed = {
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
  validate,
  validationErrors: currentErrors,
  isDirty,
  isValidating,
  isValid: computed(() => currentErrors.value.length === 0)
}

defineExpose(exposed)
</script>

<style scoped>
.p-field {
  @apply mb-4;
}

.p-field-horizontal {
  @apply flex flex-col;
}

.p-error {
  @apply text-red-500 text-sm mt-1;
}
</style>
